import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {useCart} from '../context/CartContext';
import restaurantConfig from '../config/restaurantConfig';
import {merchantApi, restaurantApi} from '../services/api';
import {useMerchant} from '../context/MerchantContext';
import {useAuth} from '../context/AuthContext';
import {useRestaurantStatus} from '../hooks/useRestaurantStatus';
import {geocodeAddress} from '../services/geolocationService';
import {getCartSuggestions} from '../services/SuggestionService';
import toast from 'react-hot-toast';
import ProductImage from './common/ProductImage';
import CartSuggestions from './CartSuggestions';


const CartSummaryContainer = styled.div`
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    padding: 0;
    width: 100%;
    position: sticky;
    top: 6rem;
    align-self: flex-start;
    max-height: calc(100vh - 7rem);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid #fff;

    @media (max-width: 992px) {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        max-height: 85vh;
        border-radius: 20px 20px 0 0;
        z-index: 1050;
        transform: translateY(100%);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        visibility: hidden;

        &.expanded {
            transform: translateY(0);
            visibility: visible;
        }
    }
`;

const Backdrop = styled.div<{ isOpen: boolean }>`
  display: none;
  @media (max-width: 992px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1040;
    backdrop-filter: blur(2px);
  }
`;

const SelectorTitle = styled.span`
  font-weight: 700;
  color: #2d3748;
  font-size: 0.9rem;
`;

const SelectorSubtitle = styled.span`
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  font-weight: 500;
`;

const ArrowIcon = styled.div`
  color: #a0522d;
  font-size: 1.2rem;
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  left: 0;
  right: 20px;
  z-index: 100;
  border-bottom: 1px solid #edf2f7;
  padding: 0.5rem;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Option = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${props => props.active ? '#f7fafc' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: #f7fafc;
  }
`;

const OptionIcon = styled.div`
  font-size: 1.5rem;
  color: #718096;
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionName = styled.span`
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
`;

const OptionDesc = styled.span`
  font-size: 0.8rem;
  color: #a0aec0;
`;

const CartItems = styled.div`
  margin-bottom: 1rem;
  max-height: 400px;
  overflow-y: auto;
`;

const CartItem = styled.div<{ isAvailable?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #fff;
  border-radius: 12px;
  border: ${props => props.isAvailable === false ? '2px solid #e53e3e' : '1px solid #edf2f7'};
  box-shadow: ${props => props.isAvailable === false ? '0 0 10px rgba(229, 62, 62, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.04)'};
  opacity: ${props => props.isAvailable === false ? 0.8 : 1};
  filter: ${props => props.isAvailable === false ? 'grayscale(0.8)' : 'none'};
  position: relative;
  overflow: hidden;
  pointer-events: ${props => props.isAvailable === false ? 'none' : 'auto'};
  
  /* Permite interagir apenas com o botão de remover quando indisponível */
  button.remove-button {
    pointer-events: auto;
  }
`;

const UnavailableBadge = styled.div`
  background-color: #e53e3e;
  color: white;
  text-align: center;
  padding: 8px 12px;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  width: 100%;
  margin-bottom: 10px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  i {
    font-size: 1rem;
  }
`;

const ItemMainRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-weight: 700;
  color: #2d3748;
  font-size: 0.95rem;
  line-height: 1.4;
  
  strong {
    margin-right: 4px;
  }
`;

const ItemExtras = styled.div`
  font-size: 0.8rem;
  color: #718096;
  margin-top: 4px;
  line-height: 1.4;
`;

const ItemPriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const ItemPrice = styled.div`
  font-weight: 700;
  color: #2d3748;
  font-size: 0.95rem;
  white-space: nowrap;
`;

const ItemImage = styled(ProductImage)`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const QtyButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #edf2f7;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #718096;
  font-size: 0.8rem;
  transition: all 0.2s;

  &:hover {
    background-color: #f7fafc;
    color: #e74c3c;
    border-color: #e74c3c;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QtyValue = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #2d3748;
  min-width: 20px;
  text-align: center;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #a0aec0;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #e74c3c;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #a0522d;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const ClearCartButton = styled.button`
  background: none;
  border: none;
  color: #2d3748;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    color: #e74c3c;
  }
`;


const TotalSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #666;
`;

const TotalLabel = styled.span``;

const TotalValue = styled.span``;

const GrandTotal = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #ddd;
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
`;

const CashbackInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.8rem;
  padding: 0.6rem 0.8rem;
  background-color: #f0fff4;
  border: 1px solid #c6f6d5;
  border-radius: 8px;
  color: #276749;
  font-size: 0.85rem;
  font-weight: 500;
  
  i {
    color: #27ae60;
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;

  &:hover {
    background-color: #c0392b;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

interface CartSummaryProps {
  // Opcional: quando não informado, o status é calculado a partir do restaurante
  // no contexto (permite renderizar o carrinho globalmente, fora da MenuPage).
  isRestaurantOpen?: boolean;
}

const CartSummary: React.FC<CartSummaryProps> = ({
  isRestaurantOpen: isRestaurantOpenProp
}) => {
  const { state, removeItem, clearCart, updateQuantity, setDeliveryFee } = useCart();
  const {
    restaurant, 
    deliveryOption, 
    setDeliveryOption, 
    selectedAddress, 
    setSelectedAddress,
    setShowAddressModal,
    setProductModalData,
    setIsCheckoutModalOpen,
    isPreCheckoutSuggestionsModalOpen,
    setIsPreCheckoutSuggestionsModalOpen,
    hasSeenSuggestions,
    setHasSeenSuggestions,
    deliveryFeeInfo,
    setDeliveryFeeInfo,
    isCartOpen,
    setIsCartOpen,
    menuCategories
  } = useMerchant();
  const { isAuthenticated, setShowLoginModal } = useAuth();

  // Status do restaurante: usa a prop quando fornecida (MenuPage) ou calcula a
  // partir do restaurante no contexto (quando renderizado globalmente).
  const { isRestaurantOpen: computeRestaurantOpen } = useRestaurantStatus(restaurant);
  const isRestaurantOpen = isRestaurantOpenProp !== undefined ? isRestaurantOpenProp : computeRestaurantOpen();

  // State for delivery selection dropdown in summary
  const [isDeliveryDropdownOpen, setIsDeliveryDropdownOpen] = useState(false);
  
  useEffect(() => {
    if (!isAuthenticated && !selectedAddress) {
      // If not authenticated, check for locally stored address
      const storedAddress = localStorage.getItem('guestAddress');
      if (storedAddress) {
        try {
          const parsedAddress = JSON.parse(storedAddress);
          setSelectedAddress(parsedAddress);
          setDeliveryOption('delivery');
          // Recalculate fee for stored address
          calculateFee(parsedAddress);
        } catch (e) {
          console.error('Failed to parse guest address', e);
        }
      }
    }
  }, [isAuthenticated, selectedAddress, setSelectedAddress, setDeliveryOption]);

  // Recalculate fee whenever selectedAddress or deliveryOption changes
  useEffect(() => {
    if (deliveryOption === 'delivery' && selectedAddress) {
      const addressKey = `${selectedAddress.zipCode || ''}-${selectedAddress.street || ''}-${selectedAddress.number || ''}`;
      if (addressKey !== lastCalculatedAddressRef.current || !deliveryFeeInfo) {
        calculateFee(selectedAddress);
      }
    } else if (deliveryOption === 'takeaway') {
      if (deliveryFeeInfo !== null) {
        setDeliveryFeeInfo(null);
        setDeliveryFee(0);
      }
    }
  }, [selectedAddress, deliveryOption, restaurant?.id]);

  const lastCalculatedAddressRef = useRef<string>('');
  
  const calculateFee = async (addr: any) => {
    if (!restaurant?.id || !addr) return;

    const addressKey = `${addr.zipCode || ''}-${addr.street || ''}-${addr.number || ''}`;
    
    // Extra guard, although redundant with useEffect check
    if (addressKey === lastCalculatedAddressRef.current && deliveryFeeInfo) {
      return;
    }

    try {
      lastCalculatedAddressRef.current = addressKey;
      const coords = await geocodeAddress(addr);
      const deliveryInfo = await merchantApi.calculateDeliveryFee(restaurant.id, coords.latitude, coords.longitude);
      let fee = typeof deliveryInfo.fee === 'number' ? deliveryInfo.fee : parseFloat(String(deliveryInfo.fee));
      
      if (isNaN(fee)) fee = 0;

      setDeliveryFeeInfo({
        fee,
        time: `${deliveryInfo.minTime}-${deliveryInfo.maxTime} min`
      });
      setDeliveryFee(fee);
    } catch (error: any) {
      console.error('Error calculating delivery info:', error);
      if (error.response?.status === 404) {
        toast.error('O restaurante não entrega para este endereço.');
      } else {
        toast.error('Não foi possível calcular a entrega para este endereço.');
      }
      setDeliveryFeeInfo(null);
      setDeliveryFee(0);
    }
  };

  const cartItemCount = state.items.reduce((count, item) => count + item.quantity, 0);
  
  // Get delivery options from restaurant or config
  const deliveryOptions = restaurant?.deliveryOptions;
  const schedulingEnabled = restaurant?.schedulingEnabled ?? false;

  const hasUnavailableItems = state.items.some(item => {
    // Check current availability against restaurant data if available
    if (restaurant && restaurant.categories) {
      // Find the product in restaurant data
      let productFromRestaurant = null;
      for (const cat of restaurant.categories) {
        if (!cat.products) continue;
        const found = cat.products.find(p => p.id === item.menuItemId);
        if (found) {
          productFromRestaurant = found;
          break;
        }
      }

      if (productFromRestaurant) {
        // Precise stock control logic:
        // 1. Merchant-level stock control must be enabled
        // 2. Product-level stock control must be enabled
        // 3. blockWithoutStock must be enabled
        // 4. Stock quantity must be 0 or less, or requested quantity exceeds available stock
        const isOutOfStock = restaurant.stockControlEnabled === true &&
          productFromRestaurant.inventory?.stockControl === true && 
          productFromRestaurant.inventory.blockWithoutStock === true && 
          (productFromRestaurant.inventory.stockQuantity !== undefined && productFromRestaurant.inventory.stockQuantity !== null) && 
          (productFromRestaurant.inventory.stockQuantity <= 0 || item.quantity > productFromRestaurant.inventory.stockQuantity);
      
        const isNotAvailable = productFromRestaurant.available === false || productFromRestaurant.status === 'UNAVAILABLE';
        return isOutOfStock || isNotAvailable;
      }
    }
  
    // Fallback to item's own state (synced by SYNC_STOCK)
    const isOutOfStock = restaurant?.stockControlEnabled === true &&
      item.inventory?.stockControl === true && 
      item.inventory.blockWithoutStock === true && 
      (item.inventory.stockQuantity !== undefined && item.inventory.stockQuantity !== null) && 
      (item.inventory.stockQuantity <= 0 || item.quantity > (item.inventory.stockQuantity || 0));
  
    const isNotAvailable = item.available === false || item.status === 'UNAVAILABLE';
    return isOutOfStock || isNotAvailable;
  });
  const subtotal = state.items.reduce((total, item) => total + (Number(item.price) * Number(item.quantity)), 0);
  
  // Set delivery fee to 0 when takeaway is selected
  const deliveryFee = deliveryOption === 'takeaway' ? 0 : (Number(deliveryFeeInfo?.fee) || 0);

  // Calculate discount based on applied coupon
  const discount = state.coupon ? 
    (Number(state.coupon.discountValue) || 
     (state.coupon.discountPercentage ? (Number(subtotal) * Number(state.coupon.discountPercentage) / 100) : 0)) 
    : 0;

  const total = Math.max(0, (Number(subtotal) || 0) + (Number(deliveryFee) || 0) - (Number(discount) || 0) - (Number(state.appliedCashback) || 0));

  // Minimum order value check
  const minimumOrderValue = restaurant?.deliveryOptions?.minimumOrderValue || 0;
  const isBelowMinimum = subtotal < minimumOrderValue;

  // Set initial delivery option based on availability if current one is invalid
  useEffect(() => {
    if (restaurant) {
      const options = restaurant.deliveryOptions || restaurantConfig.deliveryOptions;
      if (deliveryOption === 'delivery' && !options.delivery) {
        if (options.takeaway) {
          setDeliveryOption('takeaway');
        }
      } else if (deliveryOption === 'takeaway' && !options.takeaway) {
        if (options.delivery) {
          setDeliveryOption('delivery');
        }
      }
    }
  }, [restaurant, deliveryOption, setDeliveryOption]);

  const handleCheckout = () => {
    if (isBelowMinimum) {
      toast.error(`O valor mínimo para pedido é ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(minimumOrderValue)}`);
      return;
    }

    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    // Check for suggestions before opening checkout modal
    const categories = menuCategories.length > 0 
      ? menuCategories 
      : (restaurant?.categories || (restaurant as any)?.restaurantCategories);
    
    const suggestedProducts = (categories && restaurant?.id) 
      ? getCartSuggestions(state.items, categories, restaurant.id, 1) 
      : [];

    const proceedToCheckout = () => {
      // Fecha o bottom sheet do carrinho ao avançar: caso contrário, após
      // finalizar o pedido (que limpa o carrinho), o sheet ficaria aberto e
      // vazio atrás do checkout no mobile.
      setIsCartOpen(false);
      // If we already have delivery information, proceed to checkout modal
      if (selectedAddress || deliveryOption === 'takeaway') {
        if (suggestedProducts.length > 0 && !hasSeenSuggestions) {
          setIsPreCheckoutSuggestionsModalOpen(true);
          setHasSeenSuggestions(true);
        } else {
          setIsCheckoutModalOpen(true);
          setHasSeenSuggestions(true);
        }
      } else {
        // Otherwise, show the delivery selection modal
        setShowAddressModal(true);
      }
    };

    // Get delivery options from restaurant or config
    const deliveryOptions = restaurant?.deliveryOptions;

    // Check if delivery is enabled
    if (deliveryOptions?.delivery) {
      proceedToCheckout();
    } else {
      // Fecha o carrinho ao avançar (ver comentário em proceedToCheckout).
      setIsCartOpen(false);
      // If delivery is not enabled, open checkout modal or suggestions
      if (suggestedProducts.length > 0 && !hasSeenSuggestions) {
        setIsPreCheckoutSuggestionsModalOpen(true);
        setHasSeenSuggestions(true);
      } else {
        setIsCheckoutModalOpen(true);
        setHasSeenSuggestions(true);
      }
    }
  };

  const handleOptionSelect = (option: 'delivery' | 'takeaway') => {
    setDeliveryOption(option);
    setIsDeliveryDropdownOpen(false);

    if (option === 'takeaway' && restaurant?.address) {
      setSelectedAddress(restaurant.address as any);
      setDeliveryFeeInfo(null);
      setDeliveryFee(0);
    } else if (option === 'delivery') {
      // Sempre abre o modal de seleção para permitir ver o endereço atual ou trocar
      setShowAddressModal(true);
    }
  };

  const getSubtitle = () => {
    if (!deliveryOption) {
      return 'Calcular taxa e tempo de entrega';
    }

    if (deliveryOption === 'takeaway') {
      if (restaurant?.address) {
        return `${restaurant.address.street}, ${restaurant.address.number}`;
      }
      return 'Retirada no local';
    }
    
    if (selectedAddress && selectedAddress.street !== restaurant?.address?.street) {
      return `${selectedAddress.street}, ${selectedAddress.number}`;
    }
    
    return 'Calcular taxa e tempo de entrega';
  };

  const isSubtitleCalculated = () => {
    if (!deliveryOption) return false;
    if (deliveryOption === 'takeaway' && restaurant?.address) return true;
    if (deliveryOption === 'delivery' && selectedAddress && selectedAddress.street !== restaurant?.address?.street) return true;
    return false;
  };


  const handleEditItem = async (index: number) => {
    const cartItem = state.items[index];
    try {
      // Fetch the full menu item details from the API
      const menuItem = await restaurantApi.getMenuItem(state.restaurantId || '', cartItem.menuItemId);
      if (menuItem) {
        setProductModalData({ menuItem, editMode: true, editIndex: index } as any);
      }
    } catch (error) {
      console.error('Error fetching menu item for editing:', error);
    }
  };

  const toggleDropdown = () => {
    setIsDeliveryDropdownOpen(!isDeliveryDropdownOpen);
  };

  return (
    <>
      <Backdrop isOpen={isCartOpen} onClick={() => setIsCartOpen(false)} />
      <CartSummaryContainer className={isCartOpen ? 'expanded' : ''}>
      <div className="d-flex d-lg-none align-items-center justify-content-between p-3 border-bottom bg-white">
        <h3 className="h5 fw-bold m-0">Minha Sacola</h3>
        <button 
          className="btn border-0 p-2" 
          onClick={() => setIsCartOpen(false)}
          style={{ color: restaurant?.theme?.primary || '#e74c3c' }}
        >
          <i className="fas fa-times fs-4"></i>
        </button>
      </div>
      <div className="position-relative">
        <div 
          className="d-flex align-items-center justify-content-between p-3 cursor-pointer bg-white border-bottom"
          style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
          onClick={toggleDropdown}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
        >
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            {deliveryOption === 'takeaway' ? (
              <i className="fas fa-walking fs-3 text-secondary"></i>
            ) : (
              <i className="fas fa-bicycle fs-3 text-secondary"></i>
            )}
            <div className="d-flex flex-column">
              <SelectorTitle>
                {deliveryOption === 'takeaway' ? 'Retirar no local' : 'Entrega'}
              </SelectorTitle>
              <SelectorSubtitle style={{ color: isSubtitleCalculated() ? '#718096' : (restaurant?.theme?.primary || '#e74c3c') }}>
                {getSubtitle()}
              </SelectorSubtitle>
            </div>
          </div>
          <ArrowIcon>
            <i className={`fas fa-chevron-${isDeliveryDropdownOpen ? 'up' : 'right'}`}></i>
          </ArrowIcon>
        </div>

        <DropdownMenu isOpen={isDeliveryDropdownOpen}>
          <p className="m-2 small fw-bold text-dark">
            Como você quer receber o pedido?
          </p>
          {deliveryOptions?.delivery && (
            <Option 
              active={deliveryOption === 'delivery'} 
              onClick={() => handleOptionSelect('delivery')}
            >
              <OptionIcon>
                <i className="fas fa-bicycle"></i>
              </OptionIcon>
              <OptionText>
                <OptionName>Entrega</OptionName>
                <OptionDesc>A gente leva até você</OptionDesc>
              </OptionText>
            </Option>
          )}
          {deliveryOptions?.takeaway && (
            <Option 
              active={deliveryOption === 'takeaway'} 
              onClick={() => handleOptionSelect('takeaway')}
            >
              <OptionIcon>
                <i className="fas fa-walking"></i>
              </OptionIcon>
              <OptionText>
                <OptionName>Retirada</OptionName>
                <OptionDesc>Você retira no local</OptionDesc>
              </OptionText>
            </Option>
          )}
          {schedulingEnabled && (
            <div className="m-2 p-2 rounded-3 bg-primary bg-opacity-10 text-primary small d-flex align-items-center gap-2">
              <i className="fas fa-clock"></i>
              <span className="fw-medium">Agendamento disponível</span>
            </div>
          )}
        </DropdownMenu>
      </div>

      <div className="flex-grow-1 d-flex flex-column overflow-hidden border-top">
        {state.items.length === 0 ? (
          <div className="p-3 d-flex flex-column align-items-center justify-content-center py-5 text-muted gap-3">
            <div style={{ fontSize: '3rem', opacity: 0.3 }}>
              <i className="fas fa-shopping-basket"></i>
            </div>
            <span className="fw-medium">Sacola vazia</span>
          </div>
        ) : (
          <>
            <div className="p-3 pb-2 d-flex justify-content-between align-items-center">
              <h3 className="h6 fw-bold text-dark m-0">Sua sacola</h3>
              <ClearCartButton className="btn btn-link btn-sm p-0 text-decoration-none text-muted fw-bold" onClick={clearCart}>Limpar</ClearCartButton>
            </div>
            <div className="px-3 overflow-auto flex-grow-1">
              <CartItems style={{ maxHeight: 'none', marginBottom: 0 }}>
                {state.items.map((item, index) => {
                // Determine availability using the same robust logic as hasUnavailableItems
                let isAvailable = true;
                let errorMsg = '';

                if (restaurant && restaurant.categories) {
                  let productFromRestaurant = null;
                  for (const cat of restaurant.categories) {
                    if (!cat.products) continue;
                    const found = cat.products.find(p => p.id === item.menuItemId);
                    if (found) {
                      productFromRestaurant = found;
                      break;
                    }
                  }

                  if (productFromRestaurant) {
                    const stockQty = productFromRestaurant.inventory?.stockQuantity ?? 0;
                    const isOutOfStock = restaurant.stockControlEnabled === true &&
                      productFromRestaurant.inventory?.stockControl === true &&
                      productFromRestaurant.inventory.blockWithoutStock === true &&
                      (productFromRestaurant.inventory.stockQuantity !== undefined && productFromRestaurant.inventory.stockQuantity !== null) &&
                      (stockQty <= 0 || item.quantity > stockQty);
                    
                    const isNotAvailable = productFromRestaurant.available === false || productFromRestaurant.status === 'UNAVAILABLE';
                    
                    if (isNotAvailable) {
                      isAvailable = false;
                      errorMsg = 'Item não disponível';
                    } else if (isOutOfStock) {
                      isAvailable = false;
                      errorMsg = stockQty <= 0 ? 'Esgotado' : 'Estoque insuficiente';
                    }
                  }
                } else {
                  // Fallback to item state if restaurant data is not yet loaded
                  const stockQty = item.inventory?.stockQuantity ?? item.stockQuantity ?? 0;
                  const isOutOfStock = restaurant?.stockControlEnabled === true &&
                    item.inventory?.stockControl === true &&
                    item.inventory.blockWithoutStock === true &&
                    (stockQty <= 0 || item.quantity > stockQty);
                  
                  const isNotAvailable = item.available === false || item.status === 'UNAVAILABLE';
                  
                  if (isNotAvailable) {
                    isAvailable = false;
                    errorMsg = 'Item não disponível';
                  } else if (isOutOfStock) {
                    isAvailable = false;
                    errorMsg = stockQty <= 0 ? 'Esgotado' : 'Estoque insuficiente';
                  }
                }

                return (
                  <CartItem key={`${item.menuItemId}-${index}`} isAvailable={isAvailable}>
                    {!isAvailable && (
                      <UnavailableBadge>
                        <i className="fas fa-exclamation-circle"></i>
                        {errorMsg}
                      </UnavailableBadge>
                    )}
                    <ItemMainRow style={{ opacity: isAvailable ? 1 : 0.6 }}>
                      <ItemInfo>
                        <ItemName><strong>{item.quantity}x</strong> {item.name}</ItemName>
                        {(item.options && item.options.length > 0) && (
                          <ItemExtras>
                            {item.options.map((opt, i) => (
                              <span key={opt.id}>
                                {opt.name}{i < (item.options?.length || 0) - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </ItemExtras>
                        )}
                        {item.notes && <ItemExtras style={{ fontStyle: 'italic' }}>Obs: {item.notes}</ItemExtras>}
                        <QuantityControl>
                          <QtyButton 
                            onClick={() => updateQuantity(index, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <i className="fas fa-minus"></i>
                          </QtyButton>
                          <QtyValue>{item.quantity}</QtyValue>
                          <QtyButton onClick={() => updateQuantity(index, item.quantity + 1)}>
                            <i className="fas fa-plus"></i>
                          </QtyButton>
                        </QuantityControl>
                      </ItemInfo>
                      <ItemPriceColumn>
                        <ItemPrice>
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(item.price * item.quantity)}
                        </ItemPrice>
                        {item.imageUrl && (
                          <ItemImage src={item.imageUrl} alt={item.name} />
                        )}
                      </ItemPriceColumn>
                    </ItemMainRow>
                    
                    <ItemActions>
                      <EditButton 
                        onClick={() => handleEditItem(index)}
                        disabled={!isAvailable}
                        style={{ opacity: isAvailable ? 1 : 0.5, cursor: isAvailable ? 'pointer' : 'not-allowed' }}
                      >
                        Editar
                      </EditButton>
                      <RemoveButton 
                        onClick={() => removeItem(index)}
                        className="remove-button"
                      >
                        Remover
                      </RemoveButton>
                    </ItemActions>
                  </CartItem>
                );
              })}
            <CartSuggestions />
            </CartItems>
            </div>
          </>
        )}
      </div>

      {/* Coupon, Totals and Checkout always at bottom if items exist */}
      {state.items.length > 0 && (
        <div className="bg-white border-top shadow-sm">
          <div className="px-3 py-2">
            <TotalSection style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              <TotalRow>
                <TotalLabel>Subtotal</TotalLabel>
                <TotalValue>
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(subtotal)}
                </TotalValue>
              </TotalRow>
              
              {deliveryOption === 'delivery' && (
                <TotalRow>
                  <TotalLabel>Taxa de entrega</TotalLabel>
                  <TotalValue>
                    {deliveryFee > 0 ? (
                      new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(deliveryFee)
                    ) : (
                      <span style={{ color: restaurant?.theme?.primary || '#e74c3c', fontStyle: 'italic', fontWeight: 500 }}>a definir</span>
                    )}
                  </TotalValue>
                </TotalRow>
              )}

              {discount > 0 && (
                <TotalRow>
                  <TotalLabel>
                    Desconto {state.coupon && `(${state.coupon.code})`}
                  </TotalLabel>
                  <TotalValue style={{ color: restaurant?.theme?.primary || '#e74c3c' }}>
                    -{new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(discount)}
                  </TotalValue>
                </TotalRow>
              )}
              {state.appliedCashback > 0 && (
                <TotalRow>
                  <TotalLabel>Cashback utilizado</TotalLabel>
                  <TotalValue style={{ color: '#27ae60' }}>
                    -{new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(state.appliedCashback)}
                  </TotalValue>
                </TotalRow>
              )}
              <GrandTotal>
                <TotalLabel>Total</TotalLabel>
                <TotalValue>
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(total)}
                </TotalValue>
              </GrandTotal>

              {restaurant?.cashbackConfig?.enabled && total > 0 && (
                <CashbackInfo>
                  <i className="fas fa-coins"></i>
                  <span>
                    Você ganhará <strong>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                      restaurant.cashbackConfig.type === 'PERCENTAGE' 
                        ? (total * restaurant.cashbackConfig.value / 100)
                        : restaurant.cashbackConfig.value
                    )}</strong> de cashback
                  </span>
                </CashbackInfo>
              )}
            </TotalSection>
          </div>


        {isBelowMinimum && cartItemCount > 0 && (
          <div className="px-3 py-2 bg-warning bg-opacity-10 border-top border-warning border-opacity-25 d-flex align-items-center gap-2">
            <i className="fas fa-exclamation-triangle text-warning small"></i>
            <span className="small text-dark fw-bold" style={{ fontSize: '0.75rem' }}>
              Faltam {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(minimumOrderValue - subtotal)} para o mínimo.
            </span>
          </div>
        )}

        <div className="p-3">
          <CheckoutButton 
            onClick={handleCheckout} 
            disabled={cartItemCount === 0 || !isRestaurantOpen || hasUnavailableItems || isBelowMinimum}
            className="btn w-100 py-2 fw-bold rounded-3 shadow-sm border-0 text-white"
            style={{ 
              backgroundColor: hasUnavailableItems ? '#dc3545' : (cartItemCount === 0 || !isRestaurantOpen || isBelowMinimum) ? '#e9ecef' : (restaurant?.theme?.primary || '#e74c3c'),
              opacity: (cartItemCount === 0 || !isRestaurantOpen) ? 0.6 : 1,
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            {!isRestaurantOpen 
              ? 'Loja fechada' 
              : cartItemCount === 0 
                ? 'Sacola vazia' 
                : hasUnavailableItems 
                  ? 'Itens indisponíveis na sacola' 
                  : isBelowMinimum
                    ? 'Valor mínimo não atingido'
                    : 'Seguir com o pedido'}
          </CheckoutButton>
        </div>
      </div>
    )}

      {/* Product and Coupon Modals moved to App.tsx */}
      </CartSummaryContainer>
    </>
  );
};

export default CartSummary;
